import React, { useEffect, useState } from 'react';
import ParamsCard from '../../../components/Card/Params';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Database, HardDrive, Clock, CheckCircle, AlertTriangle, Power, Server, Activity, Cpu } from 'lucide-react';

const General = ({ databaseInfo }) => {
  const { projectId, databaseId } = useParams();
  const isSidebarOpen = useSelector(state => state.app.isSidebarOpen);
  const [loading, setLoading] = useState(false);
  const [databaseDetails, setDatabaseDetails] = useState(null);
  
  const databaseIdentifier = databaseInfo?.databaseName;
  
  // Define instance type specs map (approximate values for common instance types)
  const instanceSpecs = {
    // Burstable instances (T series)
    'db.t2.micro': { vcpu: 1, ram: 1, description: 'Burstable, 1 vCPU, 1 GB RAM' },
    'db.t2.small': { vcpu: 1, ram: 2, description: 'Burstable, 1 vCPU, 2 GB RAM' },
    'db.t2.medium': { vcpu: 2, ram: 4, description: 'Burstable, 2 vCPU, 4 GB RAM' },
    'db.t2.large': { vcpu: 2, ram: 8, description: 'Burstable, 2 vCPU, 8 GB RAM' },
    'db.t2.xlarge': { vcpu: 4, ram: 16, description: 'Burstable, 4 vCPU, 16 GB RAM' },
    'db.t2.2xlarge': { vcpu: 8, ram: 32, description: 'Burstable, 8 vCPU, 32 GB RAM' },
    
    'db.t3.micro': { vcpu: 2, ram: 1, description: 'Burstable, 2 vCPU, 1 GB RAM' },
    'db.t3.small': { vcpu: 2, ram: 2, description: 'Burstable, 2 vCPU, 2 GB RAM' },
    'db.t3.medium': { vcpu: 2, ram: 4, description: 'Burstable, 2 vCPU, 4 GB RAM' },
    'db.t3.large': { vcpu: 2, ram: 8, description: 'Burstable, 2 vCPU, 8 GB RAM' },
    'db.t3.xlarge': { vcpu: 4, ram: 16, description: 'Burstable, 4 vCPU, 16 GB RAM' },
    'db.t3.2xlarge': { vcpu: 8, ram: 32, description: 'Burstable, 8 vCPU, 32 GB RAM' },
    
    'db.t4g.micro': { vcpu: 2, ram: 1, description: 'Burstable (ARM), 2 vCPU, 1 GB RAM' },
    'db.t4g.small': { vcpu: 2, ram: 2, description: 'Burstable (ARM), 2 vCPU, 2 GB RAM' },
    'db.t4g.medium': { vcpu: 2, ram: 4, description: 'Burstable (ARM), 2 vCPU, 4 GB RAM' },
    'db.t4g.large': { vcpu: 2, ram: 8, description: 'Burstable (ARM), 2 vCPU, 8 GB RAM' },
    'db.t4g.xlarge': { vcpu: 4, ram: 16, description: 'Burstable (ARM), 4 vCPU, 16 GB RAM' },
    'db.t4g.2xlarge': { vcpu: 8, ram: 32, description: 'Burstable (ARM), 8 vCPU, 32 GB RAM' },
  
    // General Purpose (M series)
    'db.m5.large': { vcpu: 2, ram: 8, description: 'General Purpose, 2 vCPU, 8 GB RAM' },
    'db.m5.xlarge': { vcpu: 4, ram: 16, description: 'General Purpose, 4 vCPU, 16 GB RAM' },
    'db.m5.2xlarge': { vcpu: 8, ram: 32, description: 'General Purpose, 8 vCPU, 32 GB RAM' },
    'db.m5.4xlarge': { vcpu: 16, ram: 64, description: 'General Purpose, 16 vCPU, 64 GB RAM' },
    'db.m5.8xlarge': { vcpu: 32, ram: 128, description: 'General Purpose, 32 vCPU, 128 GB RAM' },
    'db.m5.12xlarge': { vcpu: 48, ram: 192, description: 'General Purpose, 48 vCPU, 192 GB RAM' },
    'db.m5.16xlarge': { vcpu: 64, ram: 256, description: 'General Purpose, 64 vCPU, 256 GB RAM' },
    'db.m5.24xlarge': { vcpu: 96, ram: 384, description: 'General Purpose, 96 vCPU, 384 GB RAM' },
    
    'db.m6g.large': { vcpu: 2, ram: 8, description: 'General Purpose (ARM), 2 vCPU, 8 GB RAM' },
    'db.m6g.xlarge': { vcpu: 4, ram: 16, description: 'General Purpose (ARM), 4 vCPU, 16 GB RAM' },
    'db.m6g.2xlarge': { vcpu: 8, ram: 32, description: 'General Purpose (ARM), 8 vCPU, 32 GB RAM' },
    'db.m6g.4xlarge': { vcpu: 16, ram: 64, description: 'General Purpose (ARM), 16 vCPU, 64 GB RAM' },
    'db.m6g.8xlarge': { vcpu: 32, ram: 128, description: 'General Purpose (ARM), 32 vCPU, 128 GB RAM' },
    'db.m6g.12xlarge': { vcpu: 48, ram: 192, description: 'General Purpose (ARM), 48 vCPU, 192 GB RAM' },
    'db.m6g.16xlarge': { vcpu: 64, ram: 256, description: 'General Purpose (ARM), 64 vCPU, 256 GB RAM' },
  
    // Memory Optimized (R series)
    'db.r5.large': { vcpu: 2, ram: 16, description: 'Memory Optimized, 2 vCPU, 16 GB RAM' },
    'db.r5.xlarge': { vcpu: 4, ram: 32, description: 'Memory Optimized, 4 vCPU, 32 GB RAM' },
    'db.r5.2xlarge': { vcpu: 8, ram: 64, description: 'Memory Optimized, 8 vCPU, 64 GB RAM' },
    'db.r5.4xlarge': { vcpu: 16, ram: 128, description: 'Memory Optimized, 16 vCPU, 128 GB RAM' },
    'db.r5.8xlarge': { vcpu: 32, ram: 256, description: 'Memory Optimized, 32 vCPU, 256 GB RAM' },
    'db.r5.12xlarge': { vcpu: 48, ram: 384, description: 'Memory Optimized, 48 vCPU, 384 GB RAM' },
    'db.r5.16xlarge': { vcpu: 64, ram: 512, description: 'Memory Optimized, 64 vCPU, 512 GB RAM' },
    'db.r5.24xlarge': { vcpu: 96, ram: 768, description: 'Memory Optimized, 96 vCPU, 768 GB RAM' },
    
    'db.r6g.large': { vcpu: 2, ram: 16, description: 'Memory Optimized (ARM), 2 vCPU, 16 GB RAM' },
    'db.r6g.xlarge': { vcpu: 4, ram: 32, description: 'Memory Optimized (ARM), 4 vCPU, 32 GB RAM' },
    'db.r6g.2xlarge': { vcpu: 8, ram: 64, description: 'Memory Optimized (ARM), 8 vCPU, 64 GB RAM' },
    'db.r6g.4xlarge': { vcpu: 16, ram: 128, description: 'Memory Optimized (ARM), 16 vCPU, 128 GB RAM' },
    'db.r6g.8xlarge': { vcpu: 32, ram: 256, description: 'Memory Optimized (ARM), 32 vCPU, 256 GB RAM' },
    'db.r6g.12xlarge': { vcpu: 48, ram: 384, description: 'Memory Optimized (ARM), 48 vCPU, 384 GB RAM' },
    'db.r6g.16xlarge': { vcpu: 64, ram: 512, description: 'Memory Optimized (ARM), 64 vCPU, 512 GB RAM' },
  
    // Compute Optimized (C series)
    'db.c5.large': { vcpu: 2, ram: 4, description: 'Compute Optimized, 2 vCPU, 4 GB RAM' },
    'db.c5.xlarge': { vcpu: 4, ram: 8, description: 'Compute Optimized, 4 vCPU, 8 GB RAM' },
    'db.c5.2xlarge': { vcpu: 8, ram: 16, description: 'Compute Optimized, 8 vCPU, 16 GB RAM' },
    'db.c5.4xlarge': { vcpu: 16, ram: 32, description: 'Compute Optimized, 16 vCPU, 32 GB RAM' },
    'db.c5.9xlarge': { vcpu: 36, ram: 72, description: 'Compute Optimized, 36 vCPU, 72 GB RAM' },
    'db.c5.12xlarge': { vcpu: 48, ram: 96, description: 'Compute Optimized, 48 vCPU, 96 GB RAM' },
    'db.c5.18xlarge': { vcpu: 72, ram: 144, description: 'Compute Optimized, 72 vCPU, 144 GB RAM' },
    'db.c5.24xlarge': { vcpu: 96, ram: 192, description: 'Compute Optimized, 96 vCPU, 192 GB RAM' },
    
    'db.c6g.large': { vcpu: 2, ram: 4, description: 'Compute Optimized (ARM), 2 vCPU, 4 GB RAM' },
    'db.c6g.xlarge': { vcpu: 4, ram: 8, description: 'Compute Optimized (ARM), 4 vCPU, 8 GB RAM' },
    'db.c6g.2xlarge': { vcpu: 8, ram: 16, description: 'Compute Optimized (ARM), 8 vCPU, 16 GB RAM' },
    'db.c6g.4xlarge': { vcpu: 16, ram: 32, description: 'Compute Optimized (ARM), 16 vCPU, 32 GB RAM' },
    'db.c6g.8xlarge': { vcpu: 32, ram: 64, description: 'Compute Optimized (ARM), 32 vCPU, 64 GB RAM' },
    'db.c6g.12xlarge': { vcpu: 48, ram: 96, description: 'Compute Optimized (ARM), 48 vCPU, 96 GB RAM' },
    'db.c6g.16xlarge': { vcpu: 64, ram: 128, description: 'Compute Optimized (ARM), 64 vCPU, 128 GB RAM' },
  
    // X1e (Memory Optimized for extreme cases)
    'db.x1e.xlarge': { vcpu: 4, ram: 122, description: 'Memory Optimized, 4 vCPU, 122 GB RAM' },
    'db.x1e.2xlarge': { vcpu: 8, ram: 244, description: 'Memory Optimized, 8 vCPU, 244 GB RAM' },
    'db.x1e.4xlarge': { vcpu: 16, ram: 488, description: 'Memory Optimized, 16 vCPU, 488 GB RAM' },
    'db.x1e.8xlarge': { vcpu: 32, ram: 976, description: 'Memory Optimized, 32 vCPU, 976 GB RAM' },
    'db.x1e.16xlarge': { vcpu: 64, ram: 1952, description: 'Memory Optimized, 64 vCPU, 1.9 TB RAM' },
    'db.x1e.32xlarge': { vcpu: 128, ram: 3904, description: 'Memory Optimized, 128 vCPU, 3.9 TB RAM' },
  
    // z1d (High memory and high frequency)
    'db.z1d.large': { vcpu: 2, ram: 16, description: 'High Frequency, 2 vCPU, 16 GB RAM' },
    'db.z1d.xlarge': { vcpu: 4, ram: 32, description: 'High Frequency, 4 vCPU, 32 GB RAM' },
    'db.z1d.2xlarge': { vcpu: 8, ram: 64, description: 'High Frequency, 8 vCPU, 64 GB RAM' },
    'db.z1d.3xlarge': { vcpu: 12, ram: 96, description: 'High Frequency, 12 vCPU, 96 GB RAM' },
    'db.z1d.6xlarge': { vcpu: 24, ram: 192, description: 'High Frequency, 24 vCPU, 192 GB RAM' },
    'db.z1d.12xlarge': { vcpu: 48, ram: 384, description: 'High Frequency, 48 vCPU, 384 GB RAM' },
  };
  
  // Format database status with first character uppercase
  let databaseStatus = databaseInfo?.status;
  if (databaseStatus === 'Pending') {
    databaseStatus = 'Pending';
  } else {
    databaseStatus = databaseDetails?.DBInstanceStatus 
      ? databaseDetails.DBInstanceStatus.charAt(0).toUpperCase() + databaseDetails.DBInstanceStatus.slice(1).toLowerCase()
      : 'Unknown';
  }

  useEffect(() => {
    if (databaseStatus !== 'Pending') {
      getDatabaseDetails();
    }
  }, [databaseIdentifier]);

  const getDatabaseDetails = async () => {
    if (!databaseIdentifier) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`https://cloudify-prod.wizr.in/rds/instances/${databaseIdentifier}`);
      setDatabaseDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch database details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get instance specs based on DBInstanceClass
  const getInstanceSpecs = () => {
    const instanceClass = databaseDetails?.DBInstanceClass;
    return instanceSpecs[instanceClass] || { vcpu: '?', ram: '?', description: 'Unknown instance type' };
  };

  const specs = getInstanceSpecs();

  const baseInfo = [
    {
      title: "Status",
      value: databaseStatus,
      isCopy: true,
      icon: <Activity className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Created",
      value: databaseDetails?.CreatedTime ? new Date(databaseDetails.CreatedTime).toLocaleString() : '-',
      isCopy: true,
      icon: <Clock className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Engine",
      value: databaseDetails?.Engine,
      isCopy: true,
      icon: <Database className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Engine Version",
      value: databaseDetails?.EngineVersion,
      isCopy: true,
      icon: <Database className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "vCPU",
      value: `${specs.vcpu}`,
      isCopy: true,
      icon: <Cpu className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "RAM",
      value: `${specs.ram} GB`,
      isCopy: true,
      icon: <Server className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Allocated Storage",
      value: `${databaseDetails?.AllocatedStorage} GB`,
      isCopy: true,
      icon: <HardDrive className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Provisioned IOPS",
      value: databaseDetails?.Iops || '-',
      isCopy: true,
      icon: <HardDrive className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Backup Retention Period (days)",
      value: databaseDetails?.BackupRetentionPeriod,
      isCopy: true,
      icon: <Clock className="w-5 h-5 text-indigo-500" />
    }
  ];

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-5 border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Database Configuration</h2>
              <p className="text-gray-500 text-sm mt-1">Manage and monitor your database settings</p>
            </div>
            <div className="flex gap-3">
              {specs && specs.description && (
                <div className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-md text-sm">
                  {specs.description}
                </div>
              )}
            </div>
          </div>

          <div className={`grid gap-5 ${
            isSidebarOpen 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {baseInfo.map((item, index) => (
              <ParamsCard
                key={index}
                title={item.title}
                value={item.value}
                isCopy={item.isCopy}
                icon={item.icon}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default General;